import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useTimeoutFn } from "react-use";
import StaySearchForm from "./StaySearchForm/StaySearchForm";
import { useNavigate } from "react-router-dom";
import { getMerchants } from "utils/requests/getRequest";
import SearchIcon from "@mui/icons-material/Search";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import CelebrationIcon from "@mui/icons-material/Celebration";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import {
  getMerchantsByRange,
  getMerchantsByRangeNonBookable,
} from "utils/requests/postRequests";
import {
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";

// Define playful colors
const colors = {
  primary: "#6A5ACD", // Soft purple
  secondary: "#FF9671", // Coral
  highlight: "#FFC75F", // Soft yellow
  accent: "#FF6F91", // Pink
  lightBg: "#FFF9F0", // Off-white
  border: "#FFE3B8", // Light orange
};

// Gradient options for buttons
const buttonGradients = [
  "linear-gradient(45deg, #FF6F91, #FF9671)",
  "linear-gradient(45deg, #845EC2, #D65DB1)",
  "linear-gradient(45deg, #FFC75F, #FF9671)",
  "linear-gradient(45deg, #00C9A7, #4CA1AF)",
];

const HeroSearchForm2Mobile = () => {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [activity, setActivity] = useState("party");
  const date = new Date().toLocaleString("el-GR").split(",")[0];
  const [coordinates, setCordinates] = useState({ lat: null, lng: null });
  const [selectedDate, setSelectedDate] = useState<any>(date);
  // FOR RESET ALL DATA WHEN CLICK CLEAR BUTTON
  const [showDialog, setShowDialog] = useState(false);
  let [, , resetIsShowingDialog] = useTimeoutFn(() => setShowDialog(true), 1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Randomly pick a gradient for button
  const randomGradient = React.useMemo(() => {
    return buttonGradients[Math.floor(Math.random() * buttonGradients.length)];
  }, []);

  // Animation for the decorative icon
  const [iconPosition, setIconPosition] = useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => {
      setIconPosition((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  function closeModal() {
    setShowModal(false);
  }

  const closeModalAndNavigate = async (event: any) => {
    event.preventDefault();
    let merchantData;
    if (coordinates.lat) {
      const body = {
        location: {
          lat: Number(coordinates?.lng),
          long: Number(coordinates?.lat),
          range: 1000,
        },
        is_bookable: activity === "party" ? true : false,
      };
      if (activity === "party") {
        merchantData = await getMerchantsByRange(body);
      } else {
        merchantData = await getMerchantsByRangeNonBookable(body);
      }
    } else {
      if (activity === "party") {
        merchantData = await getMerchantsByRange({ is_bookable: true });
      } else {
        merchantData = await getMerchantsByRangeNonBookable({
          is_bookable: false,
        });
      }
    }

    const buttonSubmitHref = `/listing-stay-map?category=${activity}&long=${coordinates?.lng}&lat=${coordinates?.lat}`;

    if (merchantData) {
      setShowModal(false);
      setActivity("party");
      navigate(buttonSubmitHref, {
        state: {
          data: merchantData?.rows || merchantData,
          type: activity,
        },
      });
    }
  };

  function openModal() {
    setShowModal(true);
  }

  // Decorative icons positions
  const iconPositions = [
    { top: "10%", right: "10%" },
    { top: "20%", left: "10%" },
    { bottom: "10%", right: "15%" },
    { bottom: "20%", left: "15%" },
  ];

  const renderButtonOpenModal = () => {
    return (
      <Paper
        elevation={3}
        onClick={openModal}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          borderRadius: "28px",
          p: 1,
          px: 2,
          cursor: "pointer",
          background: colors.lightBg,
          border: `2px solid ${colors.border}`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          position: "relative",
          overflow: "hidden",
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Animated background elements */}
        {[0, 1, 2, 3].map((index) => (
          <Box
            key={index}
            sx={{
              position: "absolute",
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              background: `${buttonGradients[index % buttonGradients.length]}`,
              opacity: 0.1,
              zIndex: 0,
              animation: `float${index + 1} ${3 + index}s infinite ease-in-out`,
              ...iconPositions[index % iconPositions.length],
            }}
          />
        ))}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: { xs: 36, sm: 40, md: 48 },
            height: { xs: 36, sm: 40, md: 48 },
            minWidth: { xs: 36, sm: 40, md: 48 },
            minHeight: { xs: 36, sm: 40, md: 48 },
            borderRadius: "50%",
            background: randomGradient,
            color: "white",
            zIndex: 1,
            animation: "pulse 2s infinite ease-in-out",
          }}
        >
          <TravelExploreIcon 
            sx={{ fontSize: { xs: 18, sm: 18, md: 22, lg: 24 } }} 
          />
        </Box>

        <Box sx={{ ml: 2, flexGrow: 1, zIndex: 1 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Αναζήτηση
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Οπουδήποτε • Οποιαδήποτε μέρα
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: { xs: 36, sm: 40, md: 48},
            height: { xs: 36, sm: 40, md: 48 },
            minWidth: { xs: 36, sm: 40, md: 48 },
            minHeight: { xs: 36, sm: 40, md: 48 },
            borderRadius: "50%",
            background: colors.highlight,
            color: "white",
            zIndex: 1
          }}
        >
          <CelebrationIcon sx={{ fontSize: { xs: 18, sm: 18, md: 22,  lg: 24} }}  />
        </Box>
      </Paper>
    );
  };

  return (
    <div className="HeroSearchForm2Mobile ml-2">
      {renderButtonOpenModal()}

      <Transition appear show={showModal} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog fixed inset-0 z-50"
          onClose={closeModal}
          style={{ zIndex: 999 }}
        >
          {/* Overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-black/50"
              style={{ zIndex: 9990 }}
            />
          </Transition.Child>

          <div className="fixed inset-0" style={{ zIndex: 9991 }}>
            <div className="flex h-full w-full">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-10"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-10"
              >
                <Dialog.Panel
                  className="w-full h-full transform transition-all"
                  style={{
                    background: colors.lightBg,
                    borderRadius: isMobile ? "0" : "20px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                    overflow: "hidden",
                  }}
                >
                  {showDialog && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        position: "relative",
                      }}
                    >
                      {/* Decorative elements */}
                      {[0, 1, 2, 3].map((index) => (
                        <Box
                          key={`deco-${index}`}
                          sx={{
                            position: "absolute",
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            background: `${
                              buttonGradients[index % buttonGradients.length]
                            }`,
                            opacity: 0.07,
                            zIndex: 0,
                            animation: `float${index + 1} ${
                              4 + index
                            }s infinite ease-in-out`,
                            ...iconPositions[index % iconPositions.length],
                          }}
                        />
                      ))}

                      {/* Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 2,
                          borderBottom: `2px solid ${colors.border}`,
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <IconButton
                          onClick={closeModal}
                          size="small"
                          sx={{
                            backgroundColor: colors.highlight,
                            color: "#333",
                            "&:hover": {
                              backgroundColor: colors.border,
                            },
                          }}
                        >
                          <XMarkIcon width={20} height={20} />
                        </IconButton>

                        <Typography
                          variant="h6"
                          sx={{
                            flexGrow: 1,
                            textAlign: "center",
                            fontWeight: "bold",
                            color: colors.primary,
                          }}
                        >
                          Διάλεξε διασκέδαση!
                        </Typography>

                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: colors.accent,
                            animation: "bounce 2s infinite",
                          }}
                        >
                          <EmojiEmotionsIcon />
                        </Box>
                      </Box>

                      {/* Content - CRITICAL: Set overflow-visible to show dropdowns */}
                      <Box
                        sx={{
                          flexGrow: 1,
                          p: 2,
                          overflow: "visible",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <StaySearchForm
                          setActivity={setActivity}
                          activity={activity}
                          setSelectedDate={setSelectedDate}
                          selectedDate={selectedDate}
                          setCordinates={setCordinates}
                        />
                      </Box>

                      {/* Footer */}
                      <Box
                        sx={{
                          p: 2,
                          borderTop: `2px solid ${colors.border}`,
                          display: "flex",
                          justifyContent: "center",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <Button
                          onClick={closeModalAndNavigate}
                          variant="contained"
                          fullWidth
                          size="large"
                          startIcon={<SearchIcon />}
                          sx={{
                            height: "50px",
                            background: randomGradient,
                            borderRadius: "24px",
                            fontWeight: "bold",
                            fontSize: "16px",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                            transition: "all 0.3s ease",
                            textTransform: "none",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 6px 12px rgba(0,0,0,0.2)",
                            },
                          }}
                        >
                          ΑΝΑΖΗΤΗΣΗ
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default HeroSearchForm2Mobile;
